import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PermissionContext } from "../../contexts/PermissionContext";
import { AuthContext } from "../../contexts/AuthContext";
import Notification from "../../components/UI/Notification";
import DeleteButton from "../../components/UI/AlertDialog";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import {
  FaKey,
  FaShieldAlt,
  FaEdit,
  FaTrash,
  FaPlus,
  FaUserShield,
} from "react-icons/fa";

const ManagePermissions = () => {
  const { token, roles } = useContext(AuthContext); // For authentication token and user roles
  const { permissions, loadPermissions, removePermission } =
    useContext(PermissionContext); // PermissionContext methods
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();

  // Helper function to generate role-based routes
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  // Load permissions on component mount
  useEffect(() => {
    loadPermissions(token);
  }, [token, loadPermissions]);

  // Navigate to edit permission page
  const handleEditPermission = (id) => {
    navigate(getRoleBasedRouteLocal(`/admin/edit-permission/${id}`));
  };

  const handlePermissionDelete = async (id) => {
    try {
      await removePermission(token, id);
      // setNotification("Permission deleted successfully");
      setNotification({
        message: "Permission deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete permission");
      setNotification({
        message: "Failed to delete permission",
        type: "error",
      });
    }
  };

  return (
    <div className="flex flex-col items-center">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="w-full dark:bg-gray-900 px-6 rounded">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white md:my-4">
            Manage Permissions
          </h2>
          <Link
            to={getRoleBasedRouteLocal("/admin/assign-permissions")}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-600 dark:hover:text-white transition-all duration-200 flex items-center gap-2 shadow-sm border border-blue-100 dark:border-blue-900/30"
          >
            <FaUserShield size={16} /> Assign Permissions
          </Link>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl border border-gray-200 dark:border-gray-700 mb-8 flex justify-between items-center">
          <div className="text-gray-700 dark:text-gray-200 font-medium text-lg">
            Want to define a new specific system permission?
          </div>
          <button
            onClick={() =>
              navigate(getRoleBasedRouteLocal("/admin/add-permission"))
            }
            className="px-6 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md transition duration-200 flex items-center gap-2 shadow-sm"
          >
            <FaPlus /> Create New Permission
          </button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <FaShieldAlt className="text-blue-600 dark:text-blue-400 text-2xl" />
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">
            All System Permissions
          </h3>
        </div>

        {permissions.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">
              No permissions available. Add a new permission above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {permissions.map((permission) => (
              <div
                key={permission.id}
                className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-900/50 hover:shadow-lg transition-all group relative overflow-hidden flex flex-col justify-between"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 dark:bg-blue-900/10 rounded-full -mr-12 -mt-12 transition-all group-hover:scale-110"></div>

                <div className="relative flex justify-between items-start">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                    <FaKey size={18} />
                  </div>
                  <h4 className="text-md font-bold text-gray-800 dark:text-white break-words mt-1 ml-2 text-right">
                    {permission.name}
                  </h4>
                </div>

                <div className="flex items-center gap-3 mt-3 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                  <button
                    onClick={() => handleEditPermission(permission.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-600 dark:hover:text-white rounded-lg transition-all"
                  >
                    <FaEdit size={14} /> Edit
                  </button>
                  <div className="flex-1">
                    <DeleteButton
                      delete_icon={
                        <>
                          <FaTrash size={14} /> Delete
                        </>
                      }
                      style={`w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-600 hover:text-white dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white rounded-lg transition-all`}
                      onDelete={() => handlePermissionDelete(permission.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagePermissions;
